<template>
  <div>
    <div id="product">
      <div class="product__top">
        <SfBreadcrumbs
          class="breadcrumbs desktop-only"
          :breadcrumbs="breadcrumbs"
          :loading="loader"
        />
        <WishlistButton
          v-if="product"
          @clickWishlist="() => handleAddToWishlist(product)"
          :is-in-wishlist="isInWishlist"
          :product-entity="product"
          :label="wishlistButtonLabel"
        ></WishlistButton>
      </div>
      <SimpleProduct
        v-if="product"
        :product="fullProduct"
        :loading="loader"
        :has-changed="hasChanged"
        :breadcrumbs="breadcrumbs"
      />
      <ProductAccordion
        v-if="productDescription"
        :product="product"
        :loading="loader"
      />
    </div>
    <RelatedProducts
      :products="relatedProducts"
      :is-desktop="deviceData.isDesktop"
    />
    <!--    <StructuredProductData -->
    <!--      v-if="product" -->
    <!--      :product="product" -->
    <!--      :key="`${product.merchant_sku}`" -->
    <!--    /> -->
  </div>
</template>

<script>
// import StructuredProductData from '~/components/StructuredData/StructuredProductData.vue';
import { useProduct, productGetters } from '@gemini-vsf/composables';
import {
  ref,
  computed,
  useContext,
  useRoute,
  defineComponent,
  useFetch,
  onMounted,
} from '@nuxtjs/composition-api';
import { SfBreadcrumbs } from '@storefront-ui/vue';

import { CacheTagPrefix, useCache } from '@gemini/vsf-cache';
import cacheControl from '~/helpers/cacheControl';
import { productData } from '~/helpers/product/productData';
import {
  useGtm,
  useUrlHelpers,
  useWishlist,
  useUiNotification,
} from '~/composables';
import RelatedProducts from '~/components/RelatedProducts.vue';
import SimpleProduct from '~/components/Catalog/Product/SimpleProduct.vue';
import ProductAccordion from '@/components/Catalog/Product/ProductAccordion.vue';
import WishlistButton from '~/components/Products/WishlistButton.vue';
import { useI18n } from '@/helpers/hooks/usei18n';

export default defineComponent({
  name: 'ProductPage',
  components: {
    SfBreadcrumbs,
    SimpleProduct,
    RelatedProducts,
    ProductAccordion,
    WishlistButton,
    // StructuredProductData,
  },
  middleware: cacheControl({
    'max-age': 60,
    'stale-when-revalidate': 5,
  }),
  scrollToTop: false,
  transition: 'fade',
  props: {
    productId: {
      type: [String],
      default: '',
    },
  },

  setup(props) {
    const route = useRoute();
    const id = props?.productId || route?.value?.params?.productId || '';
    const product = ref();
    const { loadItemsFromList, itemsInWishlist, toggleWishlist } =
      useWishlist();
    const { pageGtmPush, productGtmPush, addToWishlistGtmPush } = useGtm();
    const { send: sendNotification } = useUiNotification();
    const { cacheId } = productData();
    const hasChanged = ref(1);
    const { app, $vsf } = useContext();

    const deviceData = ref({});
    const translate = useI18n();
    const { locale } = translate;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/unbound-method
    const { search, products: searchResult } = useProduct(cacheId);
    const loader = ref(false);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { addTags } = useCache();

    const { buildProductUrl } = useUrlHelpers();
    const productConfiguration = ref(Object.entries(route.value.query));
    const { error: nuxtError } = useContext();
    const relatedProducts = computed(() =>
      product.value?.related_products?.length > 0
        ? product.value?.related_products
        : []
    );

    const breadcrumbs = computed(() => {
      const categories = product?.value?.categories ?? [];
      const category = productGetters
        .getBreadcrumbs(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          product.value,
          categories && categories?.length > 0
            ? categories[categories.length - 1]
            : undefined
        )
        .map((breadcrumb) => ({
          ...breadcrumb,
          link: `/${locale}${breadcrumb.link}` ?? '',
        }));

      const breadcrumbsBase = [
        {
          text: 'Shop',
          link: '',
        },
      ];

      return [...breadcrumbsBase, ...category];
    });

    const isInWishlist = computed(() => {
      return !!itemsInWishlist.value?.[product.value?.sku];
    });

    const wishlistButtonLabel = computed(() => {
      return isInWishlist.value
        ? translate.t('Remove from Wishlist')
        : translate.t('Add to your wishlist');
    });

    const productName = computed(() => product.value?.name);

    const productDescription = computed(
      () => product.value?.description?.html || ''
    );

    const productDataGtm = computed(() => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      id: productGetters.getId(product.value),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      name: productGetters.getName(product.value),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      sku: productGetters.getProductMerchantSku(product.value),
      price: productGetters.getFinalPrice(product.value),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      category: productGetters.getCategoryNameProductPage(
        product.value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        Array.isArray(product.value.categories)
          ? [...product.value.categories].pop()
          : []
      ),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      list_sku_variants: productGetters.getListVariantSku(product.value),
    }));

    const categoryId = computed(() => {
      if (!Array.isArray(product.value.categories)) {
        return '';
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const category of product.value.categories) {
        if (category.name === productDataGtm.value.category) {
          return category.uid;
        }
      }
      return '';
    });

    const fullProduct = ref(null);
    // eslint-disable-next-line consistent-return
    useFetch(async () => {
      const baseSearchQuery = {
        filter: {
          uid: id,
        },
        ...(productConfiguration.value.length > 0
          ? {
              configurations: productConfiguration.value.map(
                (config) => config[1]
              ),
            }
          : {}),
      };

      await search({
        queryType: 'DETAIL',
        ...baseSearchQuery,
        customQuery: {
          productDetail: 'productDetailCustom',
        },
      });
      if (searchResult.value?.length === 0)
        return nuxtError({ statusCode: 404 });
      product.value =
        Array.isArray(searchResult?.value?.items) &&
        searchResult?.value?.items[0]
          ? searchResult?.value?.items[0]
          : {};

      if (product?.value) {
        fullProduct.value = product.value;
        productGtmPush(product.value);
        pageGtmPush(
          product.value.name || '',
          productGetters.getProductMerchantSku(product.value) ?? null
        );

        addTags([
          { prefix: CacheTagPrefix.View, value: 'product' },
          { prefix: CacheTagPrefix.Product, value: id },
        ]);
      }

      await loadItemsFromList([product.value]);
    });

    const eventTextGtm = 'ViewContent';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const productIdTrackingContent = computed(() =>
      productGetters.getId(product.value)
    );

    const metaDescription = computed(() => {
      const descriptionRaw = product.value?.short_description?.html;
      const clenaDescription = descriptionRaw
        ?.replace(/<[^>]*>/g, '')
        ?.slice(0, 155);
      return clenaDescription;
    });

    onMounted(() => {
      if (app.$device) {
        deviceData.value = app.$device;
      }
    });

    const handleAddToWishlist = async (item) => {
      await toggleWishlist(item);
      if (isInWishlist.value) {
        sendNotification({
          id: Symbol(''),
          message: `${item.name} ${translate.t('added to wishlist')}`,
          type: 'success',
          icon: 'check',
          persist: false,
          title: 'Success',
        });
        addToWishlistGtmPush(item);
      } else {
        sendNotification({
          id: Symbol(''),
          message: `${item.name} ${translate.t('removed from wishlist')}`,
          type: 'success',
          icon: 'check',
          persist: false,
          title: 'Success',
        });
      }
    };

    return {
      breadcrumbs,
      product,
      productGetters,
      relatedProducts,
      // @typescript-eslint/no-unsafe-argument
      productDataGtm,
      loader,
      categoryId,
      metaDescription,
      eventTextGtm,
      productIdTrackingContent,
      hasChanged,
      deviceData,
      itemsInWishlist,
      productName,
      productDescription,
      toggleWishlist,
      buildProductUrl,
      handleAddToWishlist,
      fullProduct,
      isInWishlist,
      wishlistButtonLabel,
    };
  },
  head() {
    return {
      title: this.productName || 'Loading..',
      meta: [
        {
          hid: 'title',
          name: 'title',
          content: this?.product?.meta_title || this.productName || 'Loading..',
        },
        {
          hid: 'og:title',
          name: 'og:title',
          content: this?.product?.meta_title || this.productName || 'Loading..',
        },
        {
          hid: 'description',
          name: 'description',
          content: this.metaDescription ?? 'Product - Zendia',
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.metaDescription ?? 'Product - Zendia',
        },
        {
          hid: 'robots',
          name: 'robots',
          content: 'index, follow',
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.productName ?? '',
        },
      ],
    };
  },
});
</script>

<style lang="scss">
#product {
  box-sizing: border-box;
  .product {
    @include for-desktop {
      display: flex;
    }
  }
  .modals-cta {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    margin-bottom: 15px;
  }
}

.breadcrumbs {
  margin: var(--spacer-base) 0;
}

.product {
  &__info {
    margin: var(--spacer-sm) auto;
    @include for-desktop {
      margin: 0;
      width: 100%;
    }
  }

  &__header {
    --heading-title-color: var(--c-grey-8);
    --heading-title-font-weight: var(--font-weight--bold);
    --heading-padding: 0;
    margin: 0 var(--spacer-sm);
    display: flex;
    justify-content: space-between;

    .sf-heading__title {
      font-family: var(--font-family--primary);
      font-size: var(--font-size--m);
      line-height: var(--font-size--2xl);
      letter-spacing: -1.05px;
    }
    @include for-desktop {
      --heading-title-font-weight: var(--font-weight--semibold);
      margin: 0 auto;
    }
  }

  &__drag-icon {
    animation: moveicon 1s ease-in-out infinite;
    color: var(--c-text-disabled);
  }

  &__price-and-rating {
    margin: 0 var(--spacer-sm) var(--spacer-base);
    align-items: center;
    display: flex;
    justify-content: flex-start;
    gap: 30px;
    @include for-desktop {
      margin: var(--spacer-sm) 0 var(--spacer-xs) 0;
    }
    @media (max-width: $mobile-max) {
      margin: 0 0 var(--spacer-base);
      justify-content: center;
      gap: 20px;
    }
  }

  &__rating {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: var(--spacer-xs) 0 var(--spacer-xs);
  }

  &__count {
    @include font(
      --count-font,
      var(--font-weight--normal),
      var(--font-size--3xs),
      1.4,
      var(--font-family--secondary)
    );
    color: var(--c-text);
    text-decoration: none;
    margin: 0 0 0 var(--spacer-xs);
  }

  &__description {
    @include font(
      --product-description-font,
      var(--font-weight--light),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );
  }

  &__add-to-cart {
    margin: var(--spacer-base) 0 0 0;
    @include for-desktop {
      margin-top: var(--spacer-xl);
    }
  }

  ::v-deep &__add-to-cart-error {
    margin: var(--spacer-xs) 0;
  }

  &__guide,
  &__compare,
  &__save {
    display: block;
    margin: var(--spacer-xl) 0 var(--spacer-base) auto;
  }

  &__compare {
    margin-top: 0;
  }

  &__tabs {
    --tabs-title-z-index: 0;
    margin: var(--spacer-lg) auto var(--spacer-2xl);
    --tabs-title-font-size: var(--font-size--2xs);
    @include for-desktop {
      margin-top: var(--spacer-2xl);
    }
  }

  &__property {
    margin: var(--spacer-base) 0;

    &__button {
      --button-font-size: var(--font-size--base);
    }
  }

  &__review {
    padding-bottom: 24px;
    border-bottom: var(--c-light) solid 1px;
    margin-bottom: var(--spacer-base);
  }

  &__additional-info {
    color: var(--c-link);
    @include font(
      --additional-info-font,
      var(--font-weight--light),
      var(--font-size--3xs),
      1.6,
      var(--font-family--primary)
    );

    &__title {
      font-weight: var(--font-weight--normal);
      font-size: var(--font-size--base);
      margin: 0 0 var(--spacer-sm);

      &:not(:first-child) {
        margin-top: 3.5rem;
      }
    }

    &__paragraph {
      margin: 0;
    }
  }

  &__additional-actions {
    display: flex;
    justify-content: flex-start;
    margin: 0 var(--spacer-sm);
    @include for-desktop {
      margin: 0;
    }
  }

  &__gallery {
    flex: 1;
  }

  &__select-size {
    margin: 0 var(--spacer-sm);
    @include for-desktop {
      margin: 0;
    }
  }

  &__colors {
    @include font(
      --product-color-font,
      var(--font-weight--normal),
      var(--font-size--2xs),
      1.6,
      var(--font-family--secondary)
    );
    display: flex;
    align-items: center;
    margin-top: var(--spacer-xl);

    @include for-mobile {
      margin-left: var(--spacer-sm);
    }
  }

  &__color-label {
    margin: 0 var(--spacer-lg) 0 0;
  }

  &__color {
    margin: 0 var(--spacer-2xs);
  }
  &__top {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.9375rem;
  }
}

.sf-gallery {
  ::v-deep .glide__slide {
    flex: none;
  }
}

::v-deep .sf-gallery__thumbs {
  .sf-image-loaded {
    display: block;
  }
  .sf-gallery__item {
    .sf-gallery__thumb {
      .sf-image--wrapper {
        .sf-image {
          max-width: 5.625rem;
          max-height: 3.125rem;
        }
      }
    }
  }
}

@keyframes moveicon {
  0% {
    transform: translate3d(0, 0, 0) rotate(90deg) scale(1, -1);
  }
  50% {
    transform: translate3d(10%, 0, 0) rotate(90deg) scale(1, -1);
  }
  100% {
    transform: translate3d(0, 0, 0) rotate(90deg) scale(1, -1);
  }
}

.loading {
  &--product {
    padding: var(--spacer-3xl) auto;
    @include for-desktop {
      padding-top: 3.75rem;
      padding-bottom: 3.75rem;
    }
  }

  &--product-gallery {
    padding: var(--spacer-3xl) auto;
    height: max-content;
    @include for-desktop {
      padding-bottom: 3.75rem;
    }
  }
}

//big desktop
@media (min-width: $desktop-xxl-min) {
  #product {
    max-width: var(--width-boxed);
    margin: 0 auto;
    .product__info {
      padding-left: var(--spacer-2xl);
    }
  }
}

//small desktop
@media (min-width: $desktop-min) and (max-width: $desktop-xxl-min - 1) {
  #product {
    width: 80%;
    margin: 0 auto;
    .product__info {
      padding-left: 5rem;
    }
  }
}

//tablet
@media (min-width: $tablet-min) and (max-width: $tablet-max) {
  #product {
    margin: 0 var(--tablet-margin);
    .product {
      &__header {
        margin: 0 var(--spacer-xs) 0 0;
        .sf-heading__title {
          font-size: var(--font-size--xs);
          line-height: var(--font-size--sm);
        }
      }

      &__add-to-cart {
        flex-direction: column;
        gap: 10px;
      }
    }
  }
}

//mobile
@media (max-width: $mobile-max) {
  #product {
    margin: 0 var(--mobile-margin);
    .product {
      &__header {
        margin: 0 var(--spacer-xs) 0 0;
        .sf-heading__title {
          font-size: var(--font-size--xs);
          line-height: var(--font-size--sm);
        }
      }
      &__add-to-cart {
        flex-wrap: wrap;
        gap: 10px;
      }
    }
  }
}
</style>
